import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getRedemptions } from '@/api/member';
import type { MemberQuotaSnapshot, RedemptionRecord } from '@shared/api.interface';
import { Copy, ExternalLink, History, Loader2, Search } from 'lucide-react';
import { showUserError, showUserSuccess } from '@/utils/user-toast';
import { extractFirstUrl, extractShareCode } from '@/utils/url';

interface MemberHistoryDialogProps {
  passcode: string;
  triggerClassName?: string;
  /** 受控打开：从“我的会员”面板或卡片“查看资源”打开时使用 */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** 不渲染自带的触发按钮 */
  hideTrigger?: boolean;
  /** 打开时预填的搜索词（例如从某个已领取主题点进来） */
  initialQuery?: string;
}

const PAGE_SIZE = 10;

// 管理员调额只列出实际变化的计数，例如“本周期已用 3 → 2 次”
function describeQuotaChange(before?: MemberQuotaSnapshot, after?: MemberQuotaSnapshot): string {
  const fields: Array<[string, keyof MemberQuotaSnapshot]> = [
    ['本周期已用', 'cycle_used'],
    ['今日已用', 'today_used'],
    ['累计领取', 'total_redemptions'],
    ['预支下周期', 'next_cycle_borrowed'],
  ];
  const changes = fields
    .map(([label, key]) => [label, Number(before?.[key] ?? 0), Number(after?.[key] ?? 0)] as const)
    .filter(([, from, to]) => from !== to)
    .map(([label, from, to]) => `${label} ${from} → ${to} 次`);
  return changes.length ? changes.join('；') : '额度未变化';
}

function formatDateTime(value: string): string {
  if (!value) return '-';
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function MemberHistoryDialog({
  passcode,
  triggerClassName,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
  initialQuery = '',
}: MemberHistoryDialogProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (controlledOpen === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [records, setRecords] = useState<RedemptionRecord[]>([]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    async function loadRecords() {
      setLoading(true);
      try {
        const result = await getRedemptions({ passcode, pageSize: 500 });
        if (!cancelled) {
          setRecords(result.items);
        }
      } catch (error) {
        if (!cancelled) {
          setRecords([]);
          showUserError('领取记录加载失败，请稍后重试');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadRecords();

    return () => {
      cancelled = true;
    };
  }, [open, passcode]);

  useEffect(() => {
    if (open) {
      setQuery(initialQuery);
    }
    setPage(1);
  }, [open, initialQuery]);

  const normalizedQuery = query.trim().toLowerCase();
  const allVisibleRecords = records.filter(
    (record) => record.record_type === 'quota_adjustment' || record.result === 'success'
  );
  const visibleRecords = normalizedQuery
    ? allVisibleRecords.filter((record) =>
        `${record.topic_name || ''} ${record.adjustment_note || ''}`.toLowerCase().includes(normalizedQuery)
      )
    : allVisibleRecords;
  const totalPages = Math.max(1, Math.ceil(visibleRecords.length / PAGE_SIZE));
  const paginatedRecords = visibleRecords.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleCopy = async (downloadLink: string) => {
    try {
      await navigator.clipboard.writeText(downloadLink);
      showUserSuccess('已复制到剪贴板');
    } catch {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = downloadLink;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        showUserSuccess('已复制到剪贴板');
      } catch {
        showUserError('复制失败，请手动复制');
      }
    }
  };

  const handleOpen = (content: string) => {
    const url = extractFirstUrl(content);
    if (!url) {
      showUserError('没有找到可直接打开的链接，请复制后到网盘 App 打开');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // 记录不多时不显示搜索框和翻页；从某个主题点进来（带了搜索词）时仍显示搜索框，方便清空
  const hasManyRecords = allVisibleRecords.length > PAGE_SIZE;
  const showSearch = hasManyRecords || query !== '';

  return (
    <>
      {!hideTrigger && (
        <Button variant="outline" size="sm" className={triggerClassName} onClick={() => setOpen(true)}>
          <History className="mr-1 h-4 w-4" />
          领取记录
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="h-[min(90dvh,48rem)] overflow-hidden sm:h-auto sm:max-h-[90vh] sm:max-w-3xl"
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr)',
            gridTemplateRows: 'auto minmax(0, 1fr) auto',
            gap: 0,
            padding: 0,
          }}
        >
          <DialogHeader className="space-y-3 px-4 pb-3 pt-5 text-left sm:px-6 sm:pt-6">
            <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 pr-10">
              <DialogTitle className="text-lg sm:text-xl">我的领取记录</DialogTitle>
              {!loading && allVisibleRecords.length > 0 && (
                <span className="text-sm text-muted-foreground">共 {allVisibleRecords.length} 条</span>
              )}
            </div>
            <DialogDescription className="sr-only">查看已领取的资源和管理员额度调整记录</DialogDescription>
            {showSearch && (
              <label className="flex h-11 items-center gap-2 rounded-xl border border-border bg-background px-3 focus-within:border-cta focus-within:ring-2 focus-within:ring-cta/20">
                <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="sr-only">搜索领取记录</span>
                <input
                  type="search"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setPage(1);
                  }}
                  placeholder="搜主题名称"
                  className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground sm:text-sm"
                />
              </label>
            )}
          </DialogHeader>

          <div className="min-h-0 w-full min-w-0 overflow-y-auto overflow-x-hidden">
            <div className="min-w-0 space-y-2.5 px-3 pb-4 pt-1 sm:space-y-3 sm:px-6 sm:pb-6">
              {loading ? (
                <div className="flex items-center justify-center py-12 text-muted-foreground">
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  领取记录加载中...
                </div>
              ) : visibleRecords.length === 0 ? (
                <p className="py-12 text-center text-sm text-muted-foreground">
                  {normalizedQuery ? `没有找到「${query.trim()}」相关的记录` : '暂无领取或额度调整记录'}
                </p>
              ) : (
                paginatedRecords.map((record) =>
                  record.record_type === 'quota_adjustment' ? (
                    <article key={record.id} className="min-w-0 space-y-1 rounded-2xl bg-sky-50 px-4 py-3.5">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                        <h3 className="font-semibold text-sky-800">管理员调整了额度</h3>
                        <span className="whitespace-nowrap text-[13px] tabular-nums text-muted-foreground">
                          {formatDateTime(record.redemption_time)}
                        </span>
                      </div>
                      <p className="wrap-anywhere text-sm">{describeQuotaChange(record.quota_before, record.quota_after)}</p>
                      <p className="wrap-anywhere text-[13px] text-muted-foreground">
                        说明：{record.adjustment_note || '未填写调整说明'}
                      </p>
                    </article>
                  ) : (
                    <RedemptionCard
                      key={record.id}
                      record={record}
                      onCopy={handleCopy}
                      onOpenLink={handleOpen}
                    />
                  )
                )
              )}
            </div>
          </div>

          {/* 记录超过一页时才显示的固定底部分页区 */}
          {!loading && hasManyRecords && visibleRecords.length > 0 && (
            <div className="shrink-0 border-t bg-white px-4 py-3 sm:px-6">
              <div className="flex items-center justify-between gap-2 text-sm text-muted-foreground">
                <span>第 {page}/{totalPages} 页</span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((current) => current - 1)}
                  >
                    上一页
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    下一页
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// 一条成功领取记录：网盘分享文字整理成“链接 / 提取码”，复制的仍是完整原文
function RedemptionCard({
  record,
  onCopy,
  onOpenLink,
}: {
  record: RedemptionRecord;
  onCopy: (content: string) => void;
  onOpenLink: (content: string) => void;
}) {
  const content = record.download_link || '';
  const url = extractFirstUrl(content);
  const code = extractShareCode(content);

  return (
    <article className="min-w-0 space-y-3 rounded-2xl border border-border bg-card p-3.5 sm:p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <h3 className="min-w-0 wrap-anywhere text-base font-semibold text-foreground">{record.topic_name}</h3>
        <span className="whitespace-nowrap text-[13px] tabular-nums text-muted-foreground">
          {formatDateTime(record.redemption_time)}
        </span>
      </div>

      {!content ? null : url ? (
        <>
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-3.5 gap-y-1.5 text-sm">
            <dt className="text-muted-foreground">链接</dt>
            <dd className="min-w-0 truncate font-mono text-[13px]" title={url}>
              {url.replace(/^https?:\/\//, '')}
            </dd>
            {code && (
              <>
                <dt className="text-muted-foreground">提取码</dt>
                <dd>
                  <span className="rounded-md bg-claimed-soft px-2 py-px font-mono text-[15px] font-bold tracking-[2px] text-claimed">
                    {code}
                  </span>
                </dd>
              </>
            )}
          </dl>
          <div className="flex flex-wrap gap-2">
            <Button
              className="h-11 flex-[1_1_200px] rounded-xl bg-cta font-semibold text-cta-foreground hover:bg-cta-hover"
              onClick={() => onCopy(content)}
            >
              <Copy className="h-4 w-4" />
              {code ? '复制链接和提取码' : '复制链接'}
            </Button>
            <Button variant="outline" className="h-11 flex-auto rounded-xl sm:flex-none" onClick={() => onOpenLink(content)}>
              <ExternalLink className="h-4 w-4" />
              打开网盘
            </Button>
          </div>
          <details className="text-[13px] text-muted-foreground">
            <summary className="w-fit cursor-pointer py-1 underline underline-offset-[3px]">查看原文</summary>
            <p className="mt-1.5 whitespace-pre-wrap wrap-anywhere rounded-[10px] bg-background px-3 py-2.5 text-foreground">
              {content}
            </p>
          </details>
        </>
      ) : (
        <>
          <p className="whitespace-pre-wrap wrap-anywhere rounded-[10px] bg-background px-3 py-2.5 text-sm">{content}</p>
          <Button
            className="h-11 w-full rounded-xl bg-cta font-semibold text-cta-foreground hover:bg-cta-hover sm:w-auto sm:px-6"
            onClick={() => onCopy(content)}
          >
            <Copy className="h-4 w-4" />
            复制内容
          </Button>
        </>
      )}
    </article>
  );
}
